import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import InfoPage from "../InfoPage";
import { uploadFile } from "../../utils/fileUpload";
import { usePersona } from "../../contexts/PersonaContext";
import "./PartnerSettings.sass";

const emptySocials = {
  instagram: '',
  tiktok: '',
  youtube: '',
  facebook: '',
  other: '',
};

const PartnerSettings = () => {
  const navigate = useNavigate();
  const { activePersona, switchPersona } = usePersona();
  const [authorized, setAuthorized] = useState(null);
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({
    businessName: '',
    locationCity: '',
    locationState: '',
    websiteUrl: '',
    description: '',
    helpText: '',
    memberDiscountCode: '',
    logoUrl: '',
    socials: { ...emptySocials },
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Access gate — must be a partner with an active subscription
  useEffect(() => {
    let storedEmail = '';
    try { storedEmail = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    if (!storedEmail) {
      navigate('/login');
      return;
    }
    if (activePersona !== 'partner') {
      switchPersona('partner').catch(() => {});
    }
    setEmail(storedEmail);

    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: storedEmail }),
        });
        const data = await res.json();
        const roles = (data && data.roles) || [];
        if (res.ok && data.ok && data.active && roles.includes('partner')) {
          setAuthorized(true);

          try {
            const profileRes = await fetch(`/api/partner/profile?email=${encodeURIComponent(storedEmail)}`);
            const profileData = await profileRes.json();
            if (profileData.ok && profileData.profile) {
              const p = profileData.profile;
              const socials = p.socials || {};
              const getUrl = (key) => {
                const v = socials?.[key];
                if (!v) return '';
                if (typeof v === 'string') return v;
                return v.url || '';
              };
              setForm({
                businessName: p.business_name || '',
                locationCity: p.location_city || '',
                locationState: p.location_state || '',
                websiteUrl: p.website_url || '',
                description: p.description || '',
                helpText: p.help_text || '',
                memberDiscountCode: p.member_discount_code || '',
                logoUrl: p.logo_url || '',
                socials: {
                  instagram: getUrl('instagram'),
                  tiktok: getUrl('tiktok'),
                  youtube: getUrl('youtube'),
                  facebook: getUrl('facebook'),
                  other: getUrl('other'),
                },
              });
            }
          } catch (err) {
            console.error('Failed to load partner profile:', err);
          }
        } else {
          navigate('/login');
        }
      } catch (_) {
        navigate('/login');
      }
    })();
  }, [navigate, activePersona, switchPersona]);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };
  const updateSocial = (key, value) => {
    setForm((f) => ({ ...f, socials: { ...f.socials, [key]: value } }));
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setMessage(null);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, logoUrl: url }));
      setMessage({ type: 'success', text: 'Logo uploaded.' });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to upload logo. Please try again.' });
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleOpenBilling = async () => {
    if (!email || billingLoading) return;
    setBillingLoading(true);
    setMessage(null);
    try {
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok && data.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.error === 'billing_portal_not_enabled') {
        setMessage({ type: 'error', text: 'Billing portal is not enabled yet — please contact support.' });
      } else if (data?.error === 'user_not_found') {
        setMessage({ type: 'error', text: 'No billing account found for this email.' });
      } else {
        setMessage({ type: 'error', text: 'Could not open the billing portal. Please try again.' });
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      setMessage({ type: 'error', text: 'Could not open the billing portal. Please try again.' });
    } finally {
      setBillingLoading(false);
    }
  };

  const normalizeUrl = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!form.socials.instagram || !form.socials.instagram.trim()) {
      setMessage({
        type: 'error',
        text: 'Instagram URL is required — it powers your @handle on the Preferred Partners page.',
      });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const socialsPayload = {};
      for (const key of Object.keys(form.socials)) {
        const v = normalizeUrl(form.socials[key]);
        if (v) socialsPayload[key] = { url: v };
      }

      const res = await fetch('/api/partner/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          businessName: form.businessName.trim() || null,
          locationCity: form.locationCity.trim() || null,
          locationState: form.locationState.trim() || null,
          websiteUrl: normalizeUrl(form.websiteUrl) || null,
          description: form.description.trim() || null,
          helpText: form.helpText.trim() || null,
          memberDiscountCode: form.memberDiscountCode.trim() || null,
          logoUrl: form.logoUrl || null,
          socials: socialsPayload,
        }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setMessage({ type: 'success', text: 'Profile saved.' });
      } else {
        throw new Error(data?.error || 'save_failed');
      }
    } catch (err) {
      console.error('Save partner profile error:', err);
      setMessage({ type: 'error', text: 'Failed to save profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const below = (
    <div className="partner-settings">
      <h2 className="partner-settings-title">Partner Profile</h2>
      <p className="partner-settings-description">
        This is how your business appears on the Preferred Partners page.
      </p>

      <div className="partner-settings-billing">
        <div className="partner-settings-billing-info">
          <div className="partner-settings-billing-title">Billing &amp; subscription</div>
          <div className="partner-settings-billing-hint">
            Update payment method, view invoices, or cancel your partnership via
            the Stripe customer portal.
          </div>
        </div>
        <button
          type="button"
          className="partner-settings-billing-btn"
          onClick={handleOpenBilling}
          disabled={billingLoading}
        >
          {billingLoading ? 'Opening…' : 'Manage Billing'}
        </button>
      </div>

      <form className="partner-settings-form" onSubmit={handleSubmit}>
        <div className="partner-settings-logo">
          <div className="logo-preview">
            {form.logoUrl ? (
              <img src={form.logoUrl} alt="Business logo" />
            ) : (
              <div className="logo-empty">No logo<br/>yet</div>
            )}
          </div>
          <div className="logo-actions">
            <div className="logo-title">Business logo</div>
            <div className="logo-hint">Recommended: square image, at least 400 × 400px.</div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={logoUploading}
            >
              {logoUploading ? 'Uploading…' : (form.logoUrl ? 'Replace logo' : 'Upload logo')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleLogoSelect}
            />
          </div>
        </div>

        <div className="partner-settings-field">
          <label htmlFor="businessName">Business name</label>
          <input
            id="businessName"
            type="text"
            value={form.businessName}
            onChange={(e) => updateField('businessName', e.target.value)}
            placeholder="Glacier Outfitters"
          />
        </div>

        <div className="partner-settings-row">
          <div className="partner-settings-field">
            <label htmlFor="locationCity">City</label>
            <input
              id="locationCity"
              type="text"
              value={form.locationCity}
              onChange={(e) => updateField('locationCity', e.target.value)}
              placeholder="Bozeman"
            />
          </div>
          <div className="partner-settings-field">
            <label htmlFor="locationState">State</label>
            <input
              id="locationState"
              type="text"
              value={form.locationState}
              onChange={(e) => updateField('locationState', e.target.value)}
              placeholder="MT"
            />
          </div>
        </div>

        <div className="partner-settings-field">
          <label htmlFor="websiteUrl">Website</label>
          <input
            id="websiteUrl"
            type="url"
            value={form.websiteUrl}
            onChange={(e) => updateField('websiteUrl', e.target.value)}
            placeholder="https://example.com"
          />
        </div>

        <div className="partner-settings-field">
          <label htmlFor="description">Description of business</label>
          <textarea
            id="description"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Tell Postaway members about your business."
          />
        </div>

        <div className="partner-settings-field">
          <label htmlFor="helpText">How can your business help hosts/creators?</label>
          <textarea
            id="helpText"
            value={form.helpText}
            onChange={(e) => updateField('helpText', e.target.value)}
            placeholder="e.g. product partnerships, bulk pricing, co-marketing, gear rentals."
          />
        </div>

        <div className="partner-settings-field">
          <label htmlFor="memberDiscountCode">If your business is willing to offer Postaway members a discount for your products or services, please enter that code here.</label>
          <input
            id="memberDiscountCode"
            type="text"
            value={form.memberDiscountCode}
            onChange={(e) => updateField('memberDiscountCode', e.target.value)}
            placeholder="POSTAWAY15"
          />
        </div>

        <div className="partner-settings-section-title">Social media</div>

        <div className="partner-settings-row">
          <div className="partner-settings-field">
            <label htmlFor="instagram">
              Instagram URL <span style={{ color: '#dc1d08' }}>*</span>
            </label>
            <input
              id="instagram"
              type="text"
              required
              value={form.socials.instagram}
              onChange={(e) => updateSocial('instagram', e.target.value)}
              placeholder="instagram.com/yourpage"
            />
            <small style={{
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
              fontSize: 12,
              color: '#777E90',
            }}>
              Used as your @handle on the Preferred Partners page.
            </small>
          </div>
          <div className="partner-settings-field">
            <label htmlFor="tiktok">TikTok URL</label>
            <input
              id="tiktok"
              type="text"
              value={form.socials.tiktok}
              onChange={(e) => updateSocial('tiktok', e.target.value)}
              placeholder="tiktok.com/@yourpage"
            />
          </div>
        </div>

        <div className="partner-settings-row">
          <div className="partner-settings-field">
            <label htmlFor="youtube">YouTube URL</label>
            <input
              id="youtube"
              type="text"
              value={form.socials.youtube}
              onChange={(e) => updateSocial('youtube', e.target.value)}
              placeholder="youtube.com/@yourchannel"
            />
          </div>
          <div className="partner-settings-field">
            <label htmlFor="facebook">Facebook URL</label>
            <input
              id="facebook"
              type="text"
              value={form.socials.facebook}
              onChange={(e) => updateSocial('facebook', e.target.value)}
              placeholder="facebook.com/yourpage"
            />
          </div>
        </div>

        <div className="partner-settings-field">
          <label htmlFor="other">Other URL</label>
          <input
            id="other"
            type="text"
            value={form.socials.other}
            onChange={(e) => updateSocial('other', e.target.value)}
            placeholder="yoursite.com"
          />
        </div>

        {message && (
          <div className={`partner-settings-message ${message.type}`}>{message.text}</div>
        )}

        <div className="partner-settings-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/partners')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </form>
    </div>
  );

  if (authorized === null) {
    return (
      <InfoPage
        belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Loading…</div>}
        disableStamps
        customMinHeight={600}
      />
    );
  }

  return <InfoPage below={below} disableStamps customMinHeight={800} />;
};

export default PartnerSettings;
